import * as React         from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import DV                 from 'ts-data-validator';

import { nowBetweenDates } from '../../../../common/tools';
import Map                 from '../../../../common/components/map';

import { ContactsList }    from '../../../components';
import Preloader           from '../../../components/containers/preloader';
import Dropdown            from '../../../components/fields/dropdown';
import Meta                from '../../../components/meta';
import PageHeader          from '../../../components/pageHeader';
import { useCommon }       from '../../../providers/common';
import { useSiteSettings } from '../../../providers/siteSettings';
import { useWhereBuy }     from '../../../providers/whereBuy';

import renderStaticSection from '../../_common/utils/renderStaticSection';

const Contacts = function() {
  const location = useLocation();
  const { t }    = useTranslation();

  const common       = useCommon();
  const siteSettings = useSiteSettings();
  const whereBuy     = useWhereBuy();

  const [place, setPlace]                     = React.useState(null);
  const [selectedFactory, setSelectedFactory] = React.useState();
  const [selectedStore, setSelectedStore]     = React.useState();

  const city              = React.useMemo(() => {
    return whereBuy.cities.find((city) => {
      return city.urlKey === place;
    });
  }, [place, whereBuy.cities]);
  const regions           = React.useMemo(() => {
    return whereBuy.regions
      .filter((region) => {
        return whereBuy.items.some((tradeStore) => {
          return tradeStore.regionId === region.regionId;
        });
      });
  }, [whereBuy.regions, whereBuy.items]);
  const region            = React.useMemo(() => {
    return regions.find((region) => {
      return region.urlKey === place || region.regionId === city?.regionId;
    });
  }, [city, place, regions]);
  const cities            = React.useMemo(() => {
    return whereBuy.cities
      .filter((city) => {
        return !region || city.regionId === region.regionId;
      })
      .filter((city) => {
        return whereBuy.items.findIndex((store) => {
          return store.cityId === city.cityId;
        }) !== -1;
      });
  }, [region, whereBuy.cities, whereBuy.items]);
  const tradeStores       = React.useMemo(() => {
    return whereBuy.items
      .filter((tradeStore) => {
        return !region || tradeStore.regionId === region.regionId;
      })
      .filter((tradeStore) => {
        return !city || tradeStore.cityId === city.cityId;
      })
      .filter((tradeStore) => {
        return !whereBuy.typeTradeStore || tradeStore.typeTradeStore === whereBuy.typeTradeStore;
      });
  }, [city, region, whereBuy.items, whereBuy.typeTradeStore]);
  const tradeStoreTypes   = React.useMemo(() => {
    return whereBuy.types
      .filter((type) => {
        return tradeStores.findIndex((store) => {
          return store.typeTradeStore === type.valueKey;
        }) !== -1;
      })
      .sort((a, b) => {
        return a.priority < b.priority ? -1 : 1;
      });
  }, [tradeStores, whereBuy.types]);
  const selection         = React.useMemo(() => {
    let {
          lat,
          lng,
          mapZoom: zoom,
        } = whereBuy.regionData;

    if (typeof region !== 'undefined') {
      lat  = region.lat ?? lat;
      lng  = region.lng ?? lng;
      zoom = region.mapZoom ?? zoom;
    }

    if (typeof city !== 'undefined') {
      lat  = city.lat ?? lat;
      lng  = city.lng ?? lng;
      zoom = city.mapZoom ?? zoom;
    }

    // whereBuy.regionData's lat/lng/mapZoom are null unless a specific region/city is
    // selected (the backend dropped its old country-level SEO/map default — see
    // TradeStoreModel::_emptyRegionData()). With "все регионы" selected, region/city
    // are both undefined too, so without a fallback here the map used to just
    // disappear entirely. Same Belarus-wide coordinates WhereBuy.js already falls
    // back to for its own "nothing selected" case.
    lat  ??= 53.858248;
    lng  ??= 27.701393;
    zoom ??= 6;

    return {
      lat,
      lng,
      zoom,
    };
  }, [city, region, whereBuy.regionData]);
  const page              = React.useMemo(() => {
    return common.pages.find((page) => {
      return `/${page.urlKey}` === location.pathname;
    });
  }, [common.pages, location.pathname]);
  const content           = React.useMemo(() => {
    if (!page?.content) {
      return null;
    }

    try {
      return JSON.parse(page.content);
    } catch {
      return null;
    }
  }, [page]);
  const factoryMarkers    = React.useMemo(() => {
    const items = [];

    if (t('schema_company_lat').length && t('schema_company_lng').length && t('schema_company_marker').length) {
      items.push({
        address: t('schema_company_marker'),
        lat:     +t('schema_company_lat'),
        lng:     +t('schema_company_lng'),
      });
    }

    if (t('schema_regional_lat').length && t('schema_regional_lng').length && t('schema_regional_marker').length) {
      items.push({
        address: t('schema_regional_marker'),
        lat:     +t('schema_regional_lat'),
        lng:     +t('schema_regional_lng'),
      });
    }

    return items;
    // Empty deps meant this baked in whatever `t()` returned on the very first render - before
    // i18next resources finish loading, `t('schema_company_lat')` returns the raw key instead of
    // the translated value, `+key` is NaN, and it never recomputed even once translations were
    // ready, permanently breaking the factory map's center/markers.
  }, [t]);
  const factoryMapCenter  = React.useMemo(() => {
    return [
      factoryMarkers.reduce((sum, b) => (sum + b.lng), 0) / factoryMarkers.length,
      factoryMarkers.reduce((sum, b) => (sum + b.lat), 0) / factoryMarkers.length,
    ];
  }, [factoryMarkers]);
  const tradeStoreMarkers = React.useMemo(() => {
    return tradeStores.map((tradeStore) => {
      return {
        address:     tradeStore.address,
        contacts:    tradeStore.contacts,
        id:          tradeStore.tradeStoreId,
        lat:         tradeStore.lat,
        lng:         tradeStore.lng,
        workingTime: tradeStore.workTime,
      };
    });
  }, [tradeStores]);

  const changeCity   = React.useCallback((cityId) => {
    const city = cities.find((city) => {
      return city.cityId === cityId;
    });

    setPlace((!city || city.urlKey === place) ? null : city.urlKey);

    whereBuy.filterTradeStore(null);
  }, [cities, place]);
  const changeRegion = React.useCallback((regionId) => {
    const region = regions.find((region) => {
      return region.regionId === regionId;
    });

    setPlace((!region || region.urlKey === place) ? null : region.urlKey);

    whereBuy.filterTradeStore(null);
  }, [place, regions]);

  React.useEffect(() => {
    scrollTo({
      left: 0,
      top:  0,
    });

    if (page && !page.hasOwnProperty('content') && !common.loading) {
      common.getPageData(location.pathname);
    }

    if (!whereBuy.initialStateLoaded && !whereBuy.loading) {
      whereBuy.getInitialState();
    }
  }, []);

  return (
    <>
      <div className={'page-content'}>
        <Meta {...page?.meta} />
        <PageHeader>{page.label}</PageHeader>
        <main id={'contacts'}>
          {/*(BX-79013): временный хардкод микроразметки реквизитов, без доработки конструктора иначе не сохранить*/}
          <div itemScope itemType="https://schema.org/Organization">
            <meta itemProp="name" content="Частное производственно-торговое унитарное предприятие «Стальная линия»" />
            <meta itemProp="address" content="Республика Беларусь, 213105, Могилевская обл., Могилевский r-н, аг. Вейно" />
          </div>
          <div itemScope itemType="https://schema.org/Organization">
            <meta itemProp="name" content="ООО «ТД Стальная линия» - Общество с ограниченной ответственностью «Торговый Дом «Стальная линия»" />
            <meta itemProp="address" content="214031 г.Смоленск ул.Индустриальная, дом 2-А, строение 2, офис 219" />
            <meta itemProp="address" content="г. Одинцово, ул. Маршала Жукова, д. 32, оф. 411" />
          </div>

          <article>
            <section>
              <p>{t('client_weWillHelpYouChooseDoor')}</p>
            </section>
            <section className={'cols'}>
              <div>
                <h2 itemScope itemType={'http://schema.org/LocalBusiness'}>
                  {t('client_contact_center')}
                  <meta
                    itemProp={'name'}
                    content={t('schema_company_name')}
                  />
                  <meta
                    itemProp={'telephone'}
                    content={t('contact_phone_number')}
                  />
                  <meta
                    itemProp={'openingHours'}
                    content={`Mo-Fr ${siteSettings.data.WORKING_TIME.FROM}-${siteSettings.data.WORKING_TIME.TO}`}
                  />
                  <meta
                    itemProp={'address'}
                    content={t('schema_company_address')}
                  />
                </h2>
                <h4>{t('client_forDoorPurchaseQuestions')} <Link target={'_blank'} to={'/gde-kupit-kontakty'}>{t('btn_where_buy')}</Link></h4>
                <h3>{t('client_workTimeTitle')}: <br /> {siteSettings.data.WORKING_TIME.FROM} – {siteSettings.data.WORKING_TIME.TO}
                </h3>
                {siteSettings.data.WORKING_TIME.NOTE?.FROM && siteSettings.data.WORKING_TIME.NOTE?.TO && (
                  <>
                    {nowBetweenDates(siteSettings.data.WORKING_TIME.NOTE.FROM, siteSettings.data.WORKING_TIME.NOTE.TO) && (
                      <div className={'information'}>
                        <p dangerouslySetInnerHTML={{ __html: siteSettings.data.WORKING_TIME.NOTE.TEXT }} />
                      </div>
                    )}
                  </>
                )}
                <ContactsList />
              </div>
              <div itemScope itemType={'http://schema.org/Place'}>
                <meta itemProp={'name'} content={t('schema_company_name')} />
                <meta itemProp={'telephone'} content={t('contact_phone_number')} />
                <div itemScope itemType={'http://schema.org/GeoCoordinates'} itemProp={'geo'}>
                  <Map
                    selected={selectedFactory}
                    center={factoryMapCenter}
                    markers={factoryMarkers}
                    onMarkerClick={(marker) => {
                      setSelectedFactory(marker);
                    }}
                    zoom={factoryMarkers.length === 1 ? 10 : 7}
                  />
                  <meta itemProp={'latitude'} content={+t('schema_company_lat')} />
                  <meta itemProp={'longitude'} content={+t('schema_company_lng')} />
                </div>
                <div itemScope itemType={'http://schema.org/PostalAddress'} itemProp={'address'}>
                  <meta itemProp={'addressCountry'} content={t('schema_company_country')} />
                  <meta itemProp={'postalCode'} content={t('schema_company_postal_code')} />
                  <meta itemProp={'addressRegion'} content={t('schema_company_region')} />
                  <meta itemProp={'addressLocality'} content={t('schema_company_locality')} />
                </div>
              </div>
            </section>
            {DV.isNotEmptyArray(content?.SECTIONS) && (
              <div>
                {content.SECTIONS.map((section, index) => {
                  return renderStaticSection(section, index);
                })}
              </div>
            )}
          </article>
          <section className={'body'}>
            <h2>{t('client_networkOfRepresentatives')}</h2>
            <section className={'top'}>
              <div className={'bg-gray'}>
                <Dropdown
                  labelKey={'label'}
                  onChangeCallback={changeRegion}
                  options={regions}
                  placeholder={t('client_allRegions')}
                  required={false}
                  selected={region?.regionId}
                  valueKey={'regionId'}
                />
                {typeof region !== 'undefined' && (
                  <Dropdown
                    labelKey={'label'}
                    onChangeCallback={changeCity}
                    options={cities}
                    placeholder={t('client_allCities')}
                    required={false}
                    selected={city?.cityId}
                    valueKey={'cityId'}
                  />
                )}
                <Dropdown
                  labelKey={'label'}
                  onChangeCallback={whereBuy.filterTradeStore}
                  options={tradeStoreTypes}
                  placeholder={t('client_allTradeStores')}
                  required={false}
                  selected={whereBuy.typeTradeStore}
                  valueKey={'valueKey'}
                />
              </div>
            </section>
            <section>
              {selection && (
                <Map
                  selected={selectedStore}
                  center={[selection.lng, selection.lat]}
                  markers={tradeStoreMarkers}
                  onMarkerClick={(marker) => {
                    setSelectedStore(marker?.id ?? marker);
                  }}
                  zoom={selection.zoom}
                />
              )}
            </section>
          </section>
        </main>
      </div>
      {(whereBuy.loading || common.loading) && <Preloader />}
    </>
  );
};

export default Contacts;